import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DraftsCompose } from "@/drafts/drafts-compose";
import { createPendingAttachmentUploadRegistry } from "@/utils/pending-attachment-uploads";

import { type DraftQueryMetadata, useInboxQueryStates } from "./inbox-query-states-provider";
import {
  DraftsComposeContext,
  type DraftsComposeContextValue,
  type OpenDraftOptions,
  ReplyPlacement,
} from "./use-drafts-compose";

const getDraftSourcesByDraftId = (metadataByDraftId: Record<string, DraftQueryMetadata>): Record<string, string> => {
  const sourcesByDraftId: Record<string, string> = {};

  for (const [draftId, metadata] of Object.entries(metadataByDraftId)) {
    if (metadata.sourceConversationId) {
      sourcesByDraftId[draftId] = metadata.sourceConversationId;
    }
  }

  return sourcesByDraftId;
};

const getDraftPlacementsByDraftId = (
  metadataByDraftId: Record<string, DraftQueryMetadata>
): Record<string, ReplyPlacement> => {
  const placementsByDraftId: Record<string, ReplyPlacement> = {};

  for (const [draftId, metadata] of Object.entries(metadataByDraftId)) {
    if (metadata.placement === ReplyPlacement.inline) {
      placementsByDraftId[draftId] = ReplyPlacement.inline;
    } else if (metadata.placement === ReplyPlacement.floating) {
      placementsByDraftId[draftId] = ReplyPlacement.floating;
    }
  }

  return placementsByDraftId;
};

const getPrunedMetadata = (metadata: DraftQueryMetadata): DraftQueryMetadata | undefined => {
  const prunedMetadata: DraftQueryMetadata = {};

  if (metadata.sourceConversationId) {
    prunedMetadata.sourceConversationId = metadata.sourceConversationId;
  }

  if (metadata.placement) {
    prunedMetadata.placement = metadata.placement;
  }

  return prunedMetadata.sourceConversationId || prunedMetadata.placement ? prunedMetadata : undefined;
};

export const DraftsComposeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { conversationId, drafts, setDrafts, draftMetadataByDraftId, setDraftMetadataByDraftId } =
    useInboxQueryStates();
  const draftSourcesByDraftId = useMemo(
    () => getDraftSourcesByDraftId(draftMetadataByDraftId),
    [draftMetadataByDraftId]
  );
  const draftPlacementsByDraftId = useMemo(
    () => getDraftPlacementsByDraftId(draftMetadataByDraftId),
    [draftMetadataByDraftId]
  );

  // A draft editor unmounts every time its draft changes placement, so send-safety state that has
  // to outlive the swap is owned here — this provider sits above both the inline and floating
  // trees. The lazy initializer builds the registry once; it is mutable and never replaced.
  const [pendingAttachmentUploads] = useState(createPendingAttachmentUploadRegistry);

  const draftsContainerRef = useRef<HTMLDivElement>(null);
  const draftsRef = useRef(drafts);
  const draftMetadataRef = useRef(draftMetadataByDraftId);

  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useEffect(() => {
    draftMetadataRef.current = draftMetadataByDraftId;
  }, [draftMetadataByDraftId]);

  const openDrafts = useCallback(
    (draftIds: string[], options?: OpenDraftOptions) => {
      const previousMetadata = draftMetadataRef.current;
      const nextMetadata = { ...previousMetadata };
      const sourceConversationId = options?.sourceConversationId;

      for (const draftId of draftIds) {
        const existingMetadata = previousMetadata[draftId];
        const nextDraftMetadata: DraftQueryMetadata = { ...existingMetadata };

        if (sourceConversationId) {
          nextDraftMetadata.sourceConversationId = sourceConversationId;
        } else if (sourceConversationId === null) {
          nextDraftMetadata.sourceConversationId = undefined;
        }

        // A recorded placement means something already decided where this draft belongs — a reply
        // started inline, a pop-out, or a floating entry point like the drafts list or an Alby draft
        // link. Keep it. Only a draft with no metadata at all is genuinely unplaced (a URL-restored
        // id), and that is the one the thread auto-load may adopt inline. Note the source is
        // assigned above before this returns, so a floating draft still learns which thread it
        // belongs to and can be pulled inline from the close control.
        if (options?.preserveExistingPlacement && existingMetadata?.placement) {
          const prunedMetadata = getPrunedMetadata(nextDraftMetadata);

          if (prunedMetadata) {
            nextMetadata[draftId] = prunedMetadata;
          } else {
            delete nextMetadata[draftId];
          }

          continue;
        }

        // The reopeners that pass no placement — the drafts list and chat draft links — are floating
        // composer entry points, so a placement-less reopen always lands floating. Inheriting a
        // stale `inline` placement would file the draft under a thread that may not be on screen,
        // where the inline area does not match it and `floatingDrafts` skips it for being inline, so
        // the reopen would silently do nothing. Callers that need a draft's own placement kept ask
        // for it with `preserveExistingPlacement`.
        nextDraftMetadata.placement = options?.placement ?? ReplyPlacement.floating;

        const prunedMetadata = getPrunedMetadata(nextDraftMetadata);

        if (prunedMetadata) {
          nextMetadata[draftId] = prunedMetadata;
        } else {
          delete nextMetadata[draftId];
        }
      }

      draftsRef.current = [...new Set([...draftsRef.current, ...draftIds])];
      draftMetadataRef.current = nextMetadata;
      setDrafts((previousDrafts) => [...new Set([...previousDrafts, ...draftIds])]);
      setDraftMetadataByDraftId(nextMetadata);
    },
    [setDraftMetadataByDraftId, setDrafts]
  );

  const openDraft = useCallback(
    (draftId: string, options?: OpenDraftOptions) => openDrafts([draftId], options),
    [openDrafts]
  );

  const closeDrafts = useCallback(
    (draftIds: string[]) => {
      const nextMetadata = { ...draftMetadataRef.current };

      for (const draftId of draftIds) {
        delete nextMetadata[draftId];
      }

      draftsRef.current = draftsRef.current.filter((id) => !draftIds.includes(id));
      draftMetadataRef.current = nextMetadata;
      setDrafts((previousDrafts) => previousDrafts.filter((id) => !draftIds.includes(id)));
      setDraftMetadataByDraftId(nextMetadata);
    },
    [setDraftMetadataByDraftId, setDrafts]
  );

  const closeDraft = useCallback((draftId: string) => closeDrafts([draftId]), [closeDrafts]);

  // Returning a draft inline only means something while its own thread is the one on screen — that
  // is the only place `InlineDraftsCompose` renders it. A source conversation alone is not enough:
  // list-row replies stay floating but still carry one, so without the selected-conversation check
  // the close control would offer a return that leaves the draft exactly where it was (and on the
  // list page `floatingDrafts` keeps inline drafts anyway, so the click would do nothing at all).
  const canReturnDraftInline = useCallback(
    (draftId: string) => !!conversationId && draftSourcesByDraftId[draftId] === conversationId,
    [conversationId, draftSourcesByDraftId]
  );

  const returnDraftInline = useCallback(
    (draftId: string) => {
      if (!conversationId || draftMetadataRef.current[draftId]?.sourceConversationId !== conversationId) {
        return false;
      }

      openDraft(draftId, { placement: ReplyPlacement.inline });
      return true;
    },
    [conversationId, openDraft]
  );

  const getInlineDraftsForConversation = useCallback(
    (inlineConversationId?: string | null) => {
      if (!inlineConversationId) {
        return [];
      }

      return drafts.filter(
        (draftId) =>
          draftSourcesByDraftId[draftId] === inlineConversationId &&
          draftPlacementsByDraftId[draftId] === ReplyPlacement.inline
      );
    },
    [drafts, draftPlacementsByDraftId, draftSourcesByDraftId]
  );

  // Placement is per draft, not a per-conversation mode: a reply/reply-all/forward started from an
  // open conversation always begins inline, even when an earlier draft from the same thread has been
  // popped out to a floating editor. That popped-out draft keeps its own floating placement via
  // `preserveExistingPlacement`; it must not drag later drafts out of the thread with it.
  const getDraftPlacementForConversation = useCallback(
    (sourceConversationId?: string | null) => (sourceConversationId ? ReplyPlacement.inline : ReplyPlacement.floating),
    []
  );

  const floatingDrafts = useMemo(
    () => drafts.filter((draftId) => draftPlacementsByDraftId[draftId] !== ReplyPlacement.inline || !conversationId),
    [conversationId, drafts, draftPlacementsByDraftId]
  );

  const value: DraftsComposeContextValue = useMemo(
    () => ({
      openDrafts,
      openDraft,
      closeDrafts,
      closeDraft,
      canReturnDraftInline,
      returnDraftInline,
      drafts,
      floatingDrafts,
      getInlineDraftsForConversation,
      getDraftPlacementForConversation,
      draftsContainerRef,
      beginPendingAttachmentUpload: pendingAttachmentUploads.begin,
      getPendingAttachmentUploadCount: pendingAttachmentUploads.getCount,
    }),
    [
      openDrafts,
      openDraft,
      closeDrafts,
      closeDraft,
      canReturnDraftInline,
      returnDraftInline,
      drafts,
      floatingDrafts,
      getInlineDraftsForConversation,
      getDraftPlacementForConversation,
      pendingAttachmentUploads,
    ]
  );

  return (
    <DraftsComposeContext.Provider value={value}>
      {children}
      <DraftsCompose />
    </DraftsComposeContext.Provider>
  );
};
